import { DownloadIcon } from '@heroicons/react/solid';
import moment from 'moment';
import qs from 'qs';
import { useEffect, useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDebounce } from 'use-debounce';

import PrimaryButton from '../../components/Buttons/PrimaryButton';
import FiltersBar, { Filter } from '../../components/Filters/FiltersBar';
import Pagination from '../../components/Pagination';
import TitlePage from '../../components/TitlePage';
import H1Styled from '../../components/Typography/H1Styled';
import FidelityTable from './Tables/FidelityTable';

import {
  downloadFidelitiesCSV,
  getFidelities,
  getFidelityCategories,
} from '../../api/fidelity';
import sectionsAPIs from '../../api/section';
import {
  DISCOUNT_FILTER_OPTIONS,
  FIDELITY_STATUSES,
  parseDiscountValue,
} from '../../constants/fidelity';
import { useAuth } from '../../contexts/Auth';
import { FilterType } from '../../interfaces/filters';
import { FidelityStatus } from '../../interfaces/fidelity';
import { isDelegato, isDelegatoRegionale } from '../../libs/utils/auth';

interface FidelityListViewProps {
  title: string;
  statusPreset?: FidelityStatus[];
}

const FidelityListView: React.FC<FidelityListViewProps> = ({
  title,
  statusPreset,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [{ token, profile }] = useAuth();

  const search = useMemo(
    () => searchParams.get('search') || '',
    [searchParams]
  );
  const [debouncedSearch] = useDebounce(search, 500);

  const categoria = useMemo(() => {
    return Object.entries(qs.parse(searchParams.get('categoria') || ''))
      .map(([, value]) => value)
      .map((elem: any) => elem?.value)
      .filter(Boolean);
  }, [searchParams]);

  const rawStatus = useMemo(() => {
    return Object.entries(qs.parse(searchParams.get('stato') || ''))
      .map(([, value]) => value)
      .map((elem: any) => elem?.value)
      .filter(Boolean);
  }, [searchParams]);

  const appliedStatuses = useMemo(() => {
    if (statusPreset && statusPreset.length > 0) {
      if (rawStatus.length > 0) {
        const allowed = rawStatus.filter((status) =>
          statusPreset.includes(status as FidelityStatus)
        );
        return allowed.length > 0 ? allowed : statusPreset;
      }
      return statusPreset;
    }
    return rawStatus;
  }, [rawStatus, statusPreset]);

  const sconto = useMemo(() => {
    return Object.entries(qs.parse(searchParams.get('sconto') || ''))
      .map(([, value]) => value)
      .map((elem: any) => elem?.value)
      .filter(Boolean);
  }, [searchParams]);

  const scontoOnline = useMemo(
    () => searchParams.get('scontoOnline') || '',
    [searchParams]
  );
  const scontoNegozio = useMemo(
    () => searchParams.get('scontoNegozio') || '',
    [searchParams]
  );
  const tessera = useMemo(
    () => searchParams.get('tessera') || '',
    [searchParams]
  );

  const sort = useMemo(() => searchParams.get('sort') || '', [searchParams]);
  const page = useMemo(
    () => Number(searchParams.get('page') || '1'),
    [searchParams]
  );
  const pageSize = useMemo(
    () => Number(searchParams.get('pageSize') || '15'),
    [searchParams]
  );
  const sezione = useMemo(() => {
    return Object.entries(qs.parse(searchParams.get('sezione') || ''))
      .map(([, value]) => value)
      .map((elem: any) => elem?.value)
      .filter(Boolean);
  }, [searchParams]);

  const fidelityFilters = useMemo(() => {
    const filters: any = {
      pagination: {
        page,
        pageSize,
      },
      sort: sort ? JSON.parse(sort) : { createdAt: 'DESC' },
      populate: {
        sezione: {
          fields: ['name', 'slug'],
        },
        user: {
          fields: ['email'],
        },
        categorie: '*',
      },
    };

    if (debouncedSearch) {
      const searchConditions: any[] = [
        { nome: { $containsi: debouncedSearch } },
        { ragioneSociale: { $containsi: debouncedSearch } },
        { citta: { $containsi: debouncedSearch } },
        { email: { $containsi: debouncedSearch } },
        { partitaIva: { $containsi: debouncedSearch } },
      ];

      const numericSearch = Number(debouncedSearch);
      if (!Number.isNaN(numericSearch)) {
        searchConditions.push({ numeroTessera: { $eq: numericSearch } });
      }

      filters.filters = {
        ...filters.filters,
        $or: searchConditions,
      };
    }

    if (categoria.length > 0) {
      filters.filters = {
        ...filters.filters,
        categorie: {
          categoria: categoria.length === 1 ? categoria[0] : { $in: categoria },
        },
      };
    }

    if (appliedStatuses.length > 0) {
      filters.filters = {
        ...filters.filters,
        stato:
          appliedStatuses.length === 1
            ? appliedStatuses[0]
            : { $in: appliedStatuses },
      };
    }

    if (sconto.length > 0) {
      const discountConditions = sconto.flatMap((discount: string) => {
        const { min, max } = parseDiscountValue(discount);

        if (max === null) {
          return [
            { categorie: { scontoOnline: { $gte: min } } },
            { categorie: { scontoNegozio: { $gte: min } } },
          ];
        } else {
          return [
            { categorie: { scontoOnline: { $gte: min, $lte: max } } },
            { categorie: { scontoNegozio: { $gte: min, $lte: max } } },
          ];
        }
      });

      filters.filters = {
        ...filters.filters,
        $or: discountConditions,
      };
    }

    if (scontoOnline === 'true' && scontoNegozio === 'true') {
      filters.filters = {
        ...filters.filters,
        $and: [
          { categorie: { scontoOnline: { $gt: 0 } } },
          { categorie: { scontoNegozio: { $gt: 0 } } },
        ],
      };
    } else if (scontoOnline === 'true') {
      filters.filters = {
        ...filters.filters,
        categorie: {
          scontoOnline: { $gt: 0 },
        },
      };
    } else if (scontoNegozio === 'true') {
      filters.filters = {
        ...filters.filters,
        categorie: {
          scontoNegozio: { $gt: 0 },
        },
      };
    }

    if (tessera === 'true') {
      filters.filters = {
        ...filters.filters,
        numeroTessera: { $notNull: true },
      };
    }

    if (sezione.length > 0) {
      filters.filters = {
        ...filters.filters,
        sezione: {
          id:
            sezione.length === 1
              ? { $eq: parseInt(sezione[0]) }
              : { $in: sezione.map((s) => parseInt(s)) },
        },
      };
    }

    if (isDelegato(profile) && sezione.length === 0) {
      filters.filters = {
        ...filters.filters,
        sezione: {
          id: { $eq: profile?.section?.data?.id },
        },
      };
    }

    return filters;
  }, [
    appliedStatuses,
    categoria,
    debouncedSearch,
    page,
    pageSize,
    profile,
    sezione,
    sconto,
    scontoNegozio,
    scontoOnline,
    sort,
    tessera,
  ]);

  const fidelityListQuery = useQuery(
    ['fidelities', fidelityFilters],
    () => getFidelities({ query: fidelityFilters, token }),
    {
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (!statusPreset || statusPreset.length === 0) return;

    const statoParam = searchParams.get('stato');
    if (statoParam && statoParam !== '') return;

    const presetOptions = statusPreset.map((status) => {
      const preset = FIDELITY_STATUSES.find((s) => s.value === status);
      return {
        value: status,
        label: preset?.label || status,
        isHidden: 'true',
      };
    });

    const nextParams = new URLSearchParams(searchParams.toString());
    nextParams.set('stato', qs.stringify(presetOptions));
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, statusPreset]);

  const filterBar = useMemo(() => {
    const filters: Filter[] = [
      {
        type: FilterType.SEARCH_BAR,
        label: 'Cerca',
        attribute: 'search',
        key: 'search',
      },
      {
        type: FilterType.MULTISELECT,
        key: 'categoria',
        label: 'Categoria',
        attribute: 'categoria',
        source: async () => {
          const response = await getFidelityCategories();
          return {
            data: response.data.map((item) => ({
              value: item.category,
              label: item.category,
              id: item.category,
            })),
          };
        },
      },
      {
        type: FilterType.MULTISELECT,
        label: 'Sconto',
        attribute: 'sconto',
        key: 'sconto',
        source: () => ({
          data: DISCOUNT_FILTER_OPTIONS.map((option) => ({
            ...option,
            id: option.value,
            value: option.value,
          })),
        }),
      },
      {
        type: FilterType.MULTISELECT,
        label: 'Stato',
        attribute: 'stato',
        key: 'stato',
        source: () => ({
          data: (statusPreset
            ? FIDELITY_STATUSES.filter((status) =>
                statusPreset.includes(status.value)
              )
            : FIDELITY_STATUSES
          ).map((status) => ({
            value: status.value,
            label: status.label,
            id: status.value,
          })),
        }),
      },
      {
        type: FilterType.CHECKBOX,
        label: 'Sconto Online',
        attribute: 'scontoOnline',
      },
      {
        type: FilterType.CHECKBOX,
        label: 'Sconto in Negozio',
        attribute: 'scontoNegozio',
      },
      {
        type: FilterType.CHECKBOX,
        label: 'Tessera',
        attribute: 'tessera',
      },
    ];

    if (!isDelegato(profile)) {
      filters.push({
        type: FilterType.MULTISELECT,
        label: 'Sezione',
        attribute: 'sezione',
        searchForAttributes: ['name'],
        key: 'sezione',
        source: (data: any) => {
          return sectionsAPIs.findOptions({
            ...data,
            query: {
              ...data.query,
              ...(isDelegatoRegionale(profile)
                ? {
                    filters: {
                      region: {
                        id: {
                          $eq: profile?.section?.data?.attributes?.region?.data
                            ?.id,
                        },
                      },
                    },
                  }
                : {}),
            },
            token,
          });
        },
      });
    }

    return filters;
  }, [profile, token, statusPreset]);

  const { mutate: downloadCSVMutation, isLoading: isDownloadingCSV } =
    useMutation('downloadFidelitiesCSV', downloadFidelitiesCSV, {
      onError: (error: unknown) => {
        toast.error(
          error instanceof Error && error.message
            ? error.message
            : 'Errore durante il download del CSV'
        );
      },
      onSuccess: () => {
        toast.success('CSV scaricato con successo');
      },
    });

  return (
    <>
      <TitlePage title={title} />

      <div className='col-span-12 lg:col-span-9 xl:col-span-10'>
        <H1Styled>{title}</H1Styled>

        <div className='space-y-5'>
          <FiltersBar filters={filterBar} />

          <div className='flex justify-end w-full gap-4'>
            <PrimaryButton
              textSmall
              disabled={isDownloadingCSV}
              isLoading={isDownloadingCSV}
              onClick={() => {
                downloadCSVMutation({
                  token,
                  query: fidelityFilters,
                  fileName: `Fidelity ONAV ${moment().format('DD-MM-YY')}`,
                });
              }}
            >
              <span className='text-sm underline flex flex-row'>
                <DownloadIcon className='w-4 h-4 mt-[3px]' />
                <p>Scarica CSV</p>
              </span>
            </PrimaryButton>
          </div>

          <FidelityTable query={fidelityListQuery} />

          <Pagination pagination={fidelityListQuery.data?.meta.pagination} />
        </div>
      </div>
    </>
  );
};

export default FidelityListView;
